/**
 * Post a game's results to Discord: pick the server, then a text or
 * announcement channel, a thread, or a forum (a new post, with a title and
 * tags). Places the bot can't post in are listed with what it lacks, and a
 * link to invite it again with the permissions it needs. Posting where the
 * results were posted before edits that message.
 */

import { useEffect, useMemo, useState } from 'react';
import { Alert, Field, Modal, Select, Input, Checkbox } from '@swtorbit/ui/react';
import type { DiscordPostTarget, Game, ResultsPostDestination, ResultsPostDiscordResponse } from '@swtorbit/shared-socket-events';
import { useDiscordContext } from '../../contexts/DiscordContext';
import type { GameResultsActions } from '../../hooks/useGameResults';

interface DiscordPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  game: Game;
  results: GameResultsActions;
}

const KIND_GROUPS: { kind: DiscordPostTarget['kind']; label: string }[] = [
  { kind: 'text', label: 'Text channels' },
  { kind: 'announcement', label: 'Announcement channels' },
  { kind: 'forum', label: 'Forums (a new post)' },
  { kind: 'thread', label: 'Threads' },
];

function targetLabel(target: DiscordPostTarget): string {
  const where = target.kind === 'thread' && target.parentName ? `${target.parentName} › ${target.name}` : `#${target.name}`;
  const category = target.kind !== 'thread' && target.parentName ? ` (${target.parentName})` : '';
  const missing = target.missingPermissions.length > 0 ? ` (bot needs ${target.missingPermissions.join(', ')})` : '';
  return `${where}${category}${missing}`;
}

export function DiscordPostModal({ isOpen, onClose, game, results }: DiscordPostModalProps) {
  const { guilds, isLoadingGuilds, fetchGuilds, installBot, isLinked } = useDiscordContext();
  const usable = useMemo(() => {
    const list = guilds.filter((guild) => guild.isAdmin && guild.botInstalled);
    // The game's own server first.
    return [...list].sort((a, b) => Number(b.id === game.discord?.guildId) - Number(a.id === game.discord?.guildId));
  }, [guilds, game.discord?.guildId]);

  const [guildId, setGuildId] = useState('');
  const [targets, setTargets] = useState<DiscordPostTarget[] | null>(null);
  const [targetsError, setTargetsError] = useState<string | null>(null);
  const [targetId, setTargetId] = useState('');
  const [title, setTitle] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [posting, setPosting] = useState(false);
  const [outcome, setOutcome] = useState<ResultsPostDiscordResponse | null>(null);

  const publicDate = results.status?.publicDate;
  useEffect(() => {
    if (!isOpen) return;
    setOutcome(null);
    setTitle(`${game.name} · Results${publicDate ? ` · ${publicDate}` : ''}`);
    if (isLinked) fetchGuilds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Default to the game's server, or the first one.
  useEffect(() => {
    if (isOpen && !guildId && usable.length > 0) setGuildId(usable[0].id);
  }, [isOpen, guildId, usable]);

  useEffect(() => {
    if (!isOpen || !guildId) return;
    let live = true;
    setTargets(null);
    setTargetsError(null);
    setTargetId('');
    results.listTargets(guildId).then((response) => {
      if (!live) return;
      if (response.success) setTargets(response.targets ?? []);
      else setTargetsError(response.error ?? 'Could not list the channels');
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, guildId]);

  const target = targets?.find((t) => t.id === targetId) ?? null;
  const anyMissing = targets?.some((t) => t.missingPermissions.length > 0) ?? false;
  const earlier = target
    ? results.status?.posts.find((p) =>
        target.kind === 'forum' ? p.kind === 'forum_post' && p.parentChannelId === target.id : p.channelId === target.id,
      )
    : undefined;

  const post = async () => {
    if (!target) return;
    const destination: ResultsPostDestination =
      target.kind === 'forum'
        ? { kind: 'forum', channelId: target.id, title, tagIds }
        : target.kind === 'thread'
          ? { kind: 'thread', threadId: target.id }
          : { kind: 'channel', channelId: target.id };
    setPosting(true);
    setOutcome(await results.post(guildId, destination));
    setPosting(false);
  };

  const canPost = !!target && target.missingPermissions.length === 0 && (target.kind !== 'forum' || title.trim().length > 0) && !posting;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="📣 Post results to Discord"
      size="md"
      footer={
        <>
          <button className="btn btn--outline btn--slate" onClick={onClose}>
            Close
          </button>
          <button className="btn btn--discord" disabled={!canPost} onClick={post}>
            {posting ? 'Posting…' : earlier ? 'Update the post' : 'Post'}
          </button>
        </>
      }
    >
      <div className="d-flex flex-column gap-sm">
        {!isLinked && <Alert hue="amber">Link your Discord account (Discord tab) to post to your servers.</Alert>}

        <Field label="Server" htmlFor="results-post-guild">
          <Select id="results-post-guild" value={guildId} disabled={isLoadingGuilds || usable.length === 0} onChange={(e) => setGuildId(e.target.value)}>
            {usable.length === 0 && <option value="">{isLoadingGuilds ? 'Loading…' : 'No servers with the bot'}</option>}
            {usable.map((guild) => (
              <option key={guild.id} value={guild.id}>
                {guild.name}
                {guild.id === game.discord?.guildId ? ' (this game’s server)' : ''}
              </option>
            ))}
          </Select>
        </Field>

        {targetsError && <Alert hue="red">{targetsError}</Alert>}

        <Field label="Post to" htmlFor="results-post-target" hint={earlier ? 'The results were posted here before: that message is updated.' : undefined}>
          <Select id="results-post-target" value={targetId} disabled={!targets} onChange={(e) => setTargetId(e.target.value)}>
            <option value="">{targets ? 'Pick a channel, thread or forum' : 'Loading channels…'}</option>
            {KIND_GROUPS.map(({ kind, label }) => {
              const group = targets?.filter((t) => t.kind === kind) ?? [];
              if (group.length === 0) return null;
              return (
                <optgroup key={kind} label={label}>
                  {group.map((t) => (
                    <option key={t.id} value={t.id} disabled={t.missingPermissions.length > 0}>
                      {targetLabel(t)}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </Select>
        </Field>

        {target?.kind === 'forum' && !earlier && (
          <>
            <Field label="Post title" htmlFor="results-post-title">
              <Input id="results-post-title" value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            {target.tags && target.tags.length > 0 && (
              <Field label="Tags" hint="Up to 5. Moderator-only tags can't be applied by the bot.">
                <div className="d-flex flex-wrap gap-sm">
                  {target.tags.map((tag) => (
                    <Checkbox
                      key={tag.id}
                      label={`${tag.emoji ? `${tag.emoji} ` : ''}${tag.name}`}
                      checked={tagIds.includes(tag.id)}
                      disabled={tag.moderated || (!tagIds.includes(tag.id) && tagIds.length >= 5)}
                      onChange={(e) => setTagIds((ids) => (e.target.checked ? [...ids, tag.id] : ids.filter((id) => id !== tag.id)))}
                    />
                  ))}
                </div>
              </Field>
            )}
          </>
        )}

        {anyMissing && (
          <p className="fs-sm text-slate m-0">
            The bot can&apos;t post in some places.{' '}
            <button type="button" className="btn btn--ghost btn--cyan btn--xs" onClick={() => installBot(guildId)}>
              Invite it again
            </button>{' '}
            to give it the permissions it needs, or give its role them in Discord.
          </p>
        )}

        {outcome?.success && (
          <Alert hue="green" title={outcome.edited ? 'Post updated' : 'Posted'}>
            {outcome.messageUrl && (
              <a href={outcome.messageUrl} target="_blank" rel="noreferrer">
                Open it in Discord
              </a>
            )}
            {outcome.warnings?.map((warning) => (
              <div key={warning} className="fs-sm">
                {warning}
              </div>
            ))}
          </Alert>
        )}
        {outcome && !outcome.success && <Alert hue="red">{outcome.error}</Alert>}
      </div>
    </Modal>
  );
}
